import React from 'react';

/**
 * ProgressBar
 * Thin horizontal progress indicator.
 *
 * Props:
 *   value: 0–100 (clamped)
 *   tone: 'neutral'|'success'|'warning'|'danger' (default 'neutral')
 *   height: 'xs'|'sm'|'md' (default 'xs')
 *   showLabel: boolean — renders "{value}%" to the right
 *   ariaLabel: string
 */
export default function ProgressBar({
  value,
  tone = 'neutral',
  height = 'xs',
  showLabel = false,
  ariaLabel
}) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));

  const heightClass = {
    xs: 'h-1',
    sm: 'h-1.5',
    md: 'h-2'
  }[height] || 'h-1';

  const toneClass = {
    neutral: 'bg-gray-400',
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    danger: 'bg-red-500'
  }[tone] || 'bg-gray-400';

  return (
    <div className="flex items-center gap-2">
      <div
        className={`relative flex-1 bg-gray-100 rounded-full overflow-hidden ${heightClass}`}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
      >
        <div
          className={`h-full ${toneClass} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-xs text-gray-500 tabular-nums shrink-0">{pct.toFixed(0)}%</span>
      )}
    </div>
  );
}
